import React from 'react';
import { Image } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { I3Square, CloseCircle } from 'iconsax-react';
import { useTranslation } from 'react-i18next';

import TitleText from '@/components/atoms/TitleText';
import InfoText from '@/components/atoms/InfoText';
import LoaderSpin from '@/components/atoms/LoaderSpin';

import { UploadedImage, formatBytes } from './useImageUploader';

interface SortableImageRowProps {
  image: UploadedImage;
  isThumbnail: boolean;
  onSetThumbnail: (id: string) => void;
  onRemove: (id: string) => void;
  disableReorder?: boolean;
}

export function SortableImageRow({
  image,
  isThumbnail,
  onSetThumbnail,
  onRemove,
  disableReorder = false,
}: SortableImageRowProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: image.id,
  });

  const dragStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      data-testid={`image-uploader-item-${image.id}`}
      className={`
        flex items-center gap-3 sm:gap-4 py-2 px-3 sm:px-4 transition-colors group/row
        bg-white dark:bg-neutral-900 border-b border-gray-100 dark:border-neutral-800 last:border-b-0 hover:bg-gray-50 dark:hover:bg-neutral-800
        ${isDragging ? 'shadow-md border border-gray-200 dark:border-neutral-700' : ''}
      `}
    >
      {/* Drag handle */}
      {!disableReorder && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={image.uploading}
          data-testid={`image-uploader-drag-handle-${image.id}`}
          aria-label={t('dragToReorder')}
          className={`cursor-grab active:cursor-grabbing text-gray-400 dark:text-neutral-500
            hover:text-gray-600 dark:hover:text-neutral-300 p-1 -ml-1 touch-none flex-shrink-0
            ${image.uploading ? 'pointer-events-none opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <I3Square size="20" variant="Bold" className="!text-text-secondary" />
        </button>
      )}

      {/* Thumbnail preview */}
      <div
        data-testid={`image-uploader-preview-${image.id}`}
        className="relative w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden flex-shrink-0
          border border-gray-200 dark:border-neutral-700 bg-gray-100 dark:bg-neutral-800"
      >
        <Image
          src={image.preview}
          alt={image.file?.name ?? image.id}
          className="object-contain p-1 w-full h-full"
          preview={false}
        />
        {image.uploading && (
          <div
            data-testid={`image-uploader-loading-overlay-${image.id}`}
            className="absolute inset-0 bg-black/40 flex items-center justify-center"
          >
            <LoaderSpin size="small" data-testid={`image-uploader-spinner-${image.id}`} />
          </div>
        )}
      </div>

      {/* File name + size */}
      <div data-testid={`image-uploader-info-${image.id}`} className="flex-1 min-w-0">
        <TitleText
          text={image.file?.name ?? image.id}
          size="sm"
          className="font-medium text-neutral-800 dark:text-neutral-100 truncate"
        />
        <InfoText
          text={image.file ? formatBytes(image.file.size) : ''}
          size="xs"
          className="text-gray-400 dark:text-neutral-500 mt-0.5"
        />
      </div>

      {/* Actions — visible on hover or always for the thumbnail */}
      <div
        data-testid={`image-uploader-actions-${image.id}`}
        className={`flex items-center gap-3 sm:gap-4 transition-opacity
          ${isThumbnail ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100'}
          ${image.uploading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        {isThumbnail ? (
          <InfoText
            text={t('thumbnailLabel')}
            size="xs"
            className="font-medium text-gray-700 dark:text-neutral-300 whitespace-nowrap"
          />
        ) : (
          <button
            type="button"
            onClick={() => onSetThumbnail(image.id)}
            disabled={image.uploading}
            data-testid={`image-uploader-thumbnail-btn-${image.id}`}
            className="text-[12px] sm:text-[13px] font-medium text-gray-600 dark:text-neutral-400
              hover:text-gray-900 dark:hover:text-neutral-100 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            {t('setThumbnail')}
          </button>
        )}

        <button
          type="button"
          onClick={() => onRemove(image.id)}
          disabled={image.uploading}
          data-testid={`image-uploader-remove-btn-${image.id}`}
          aria-label={t('removeImage')}
          className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300
            transition-colors flex-shrink-0 flex items-center justify-center disabled:opacity-50"
        >
          <CloseCircle size="20" variant="Outline" />
        </button>
      </div>
    </div>
  );
}
